import { useCallback, useEffect, useMemo, useState } from "react";
import { useCanAccess, useTranslate } from "ra-core";
import { Link, useParams } from "react-router";
import { ArrowLeft, Maximize2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Markdown } from "../misc/Markdown";
import { useExperimentManifest } from "./api";
import { ExperimentGallery } from "./ExperimentGallery";
import { ExperimentImage, useExperimentImageUrl } from "./ExperimentImage";
import { LabResultsPage, formatDate } from "./LabResultsPage";
import {
  formatMetric,
  isGalleryExperiment,
  isVideoExperiment,
  orderedArms,
} from "./manifest";
import { PlaygroundPanel } from "./PlaygroundPanel";
import { SyncedVideoCompare } from "./SyncedVideoCompare";
import { imageKey } from "./types";
import type {
  ExperimentArm,
  ExperimentManifest,
  ExperimentPhoto,
} from "./types";

export const LabResultShowPage = () => {
  const translate = useTranslate();
  const { slug } = useParams<{ slug: string }>();
  const { index, summary, manifest } = useExperimentManifest(slug);

  if (index.isLoading || (summary && manifest.isLoading)) {
    return (
      <div className="max-w-7xl mx-auto my-8 px-4 space-y-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-[60vh]" />
      </div>
    );
  }
  const error = index.error ?? manifest.error;
  if (error || !summary || !manifest.data) {
    return (
      <div className="max-w-3xl mx-auto my-8 px-4">
        <BackLink />
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            {error
              ? `${translate("crm.lab_results.load_error")}: ${(error as Error).message}`
              : translate("crm.lab_results.not_found")}
          </CardContent>
        </Card>
      </div>
    );
  }
  return <ExperimentViewer manifest={manifest.data} />;
};

LabResultShowPage.path = `${LabResultsPage.path}/:slug`;

const BackLink = () => {
  const translate = useTranslate();
  return (
    <Link
      to={LabResultsPage.path}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
    >
      <ArrowLeft className="h-4 w-4" /> {translate("crm.lab_results.back")}
    </Link>
  );
};

const kindVariant = (kind?: ExperimentArm["kind"]) =>
  kind === "source" ? "outline" : kind === "control" ? "secondary" : "default";

export const ExperimentViewer = ({
  manifest,
}: {
  manifest: ExperimentManifest;
}) => {
  const translate = useTranslate();
  const arms = useMemo(() => orderedArms(manifest), [manifest]);
  const video = isVideoExperiment(manifest);
  const gallery = isGalleryExperiment(manifest);
  const photos = manifest.photos;
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightbox, setLightbox] = useState<{ armIndex: number } | null>(null);
  const [view, setView] = useState<"compare" | "wipe" | "grid" | "playground">(
    "compare",
  );
  // Generating spends tower GPU time: administrators only (the API enforces it too).
  const playgroundAccess = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const playground =
    !video && manifest.playground && playgroundAccess.canAccess
      ? manifest.playground
      : undefined;
  const photo = photos[Math.min(photoIndex, photos.length - 1)];
  const media = useCallback(
    (p: ExperimentPhoto, arm: ExperimentArm) =>
      manifest.images[imageKey(p.id, arm.id)],
    [manifest],
  );

  useEffect(() => {
    // The gallery shows every item at once; arrow keys only move the single-item views.
    if (lightbox || gallery) return;
    const onKey = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      )
        return;
      if (event.key === "ArrowRight")
        setPhotoIndex((i) => Math.min(i + 1, photos.length - 1));
      if (event.key === "ArrowLeft") setPhotoIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gallery, lightbox, photos.length]);

  return (
    <div className="max-w-[1800px] mx-auto my-6 px-4 space-y-6">
      <div>
        <BackLink />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">{manifest.title}</h1>
            <p className="text-sm text-muted-foreground">
              {formatDate(manifest.date)}
              {manifest.author ? ` · ${manifest.author}` : ""} ·{" "}
              {translate(
                video ? "crm.lab_results.videos" : "crm.lab_results.photos",
                { smart_count: photos.length },
              )}
              {!gallery &&
                ` · ${translate("crm.lab_results.arms", { smart_count: arms.length })}`}
            </p>
            {manifest.summary && (
              <p className="text-sm max-w-3xl">{manifest.summary}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {manifest.tags?.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {gallery ? (
        <ExperimentGallery manifest={manifest} arms={arms} />
      ) : (
        <>
          <Card>
            <CardContent className="py-4">
              <h2 className="text-sm font-semibold mb-2">
                {translate("crm.lab_results.legend")}
              </h2>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {arms.map((arm) => (
                  <div key={arm.id} className="flex items-start gap-2 text-sm">
                    <Badge
                      variant={kindVariant(arm.kind)}
                      className="mt-0.5 shrink-0"
                    >
                      {arm.label}
                    </Badge>
                    <span className="text-muted-foreground">
                      {arm.description}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <PhotoStrip
            photos={photos}
            arms={arms}
            manifest={manifest}
            selected={photoIndex}
            onSelect={setPhotoIndex}
            pickLabel={translate(
              video
                ? "crm.lab_results.pick_video"
                : "crm.lab_results.pick_photo",
            )}
          />

          <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
            <TabsList>
              <TabsTrigger value="compare">
                {translate("crm.lab_results.view_compare")}
              </TabsTrigger>
              {!video && (
                <TabsTrigger value="wipe">
                  {translate("crm.lab_results.view_wipe")}
                </TabsTrigger>
              )}
              <TabsTrigger value="grid">
                {translate("crm.lab_results.view_grid")}
              </TabsTrigger>
              {playground && (
                <TabsTrigger value="playground">
                  {translate("crm.lab_results.playground.tab")}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="compare">
              {photo && video && (
                <SyncedVideoCompare
                  manifest={manifest}
                  arms={arms}
                  photo={photo}
                />
              )}
              {photo && !video && (
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                  {arms.map((arm, armIndex) => (
                    <figure key={arm.id} className="space-y-1">
                      <figcaption className="flex items-center justify-between text-sm">
                        <Badge variant={kindVariant(arm.kind)}>
                          {arm.label}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7"
                          onClick={() => setLightbox({ armIndex })}
                          title={translate("crm.lab_results.open_full")}
                        >
                          <Maximize2 className="h-4 w-4" />
                        </Button>
                      </figcaption>
                      <div className="bg-black/90 rounded-md overflow-hidden flex items-center justify-center">
                        <ExperimentImage
                          media={media(photo, arm)}
                          alt={`${photo.label ?? photo.id} – ${arm.label}`}
                          className="w-full max-h-[70vh] object-contain cursor-zoom-in"
                          onClick={() => setLightbox({ armIndex })}
                        />
                      </div>
                      {arm.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {arm.description}
                        </p>
                      )}
                    </figure>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="wipe">
              {photo && (
                <WipeCompare photo={photo} arms={arms} manifest={manifest} />
              )}
            </TabsContent>
            {playground && photo && (
              <TabsContent value="playground">
                <PlaygroundPanel
                  manifest={manifest}
                  playground={playground}
                  photo={photo}
                />
              </TabsContent>
            )}
            <TabsContent value="grid">
              <GridView
                photos={photos}
                arms={arms}
                manifest={manifest}
                onOpen={(pi, ai) => {
                  setPhotoIndex(pi);
                  // Films open in the synced compare; photos in the full-resolution lightbox.
                  if (video) setView("compare");
                  else setLightbox({ armIndex: ai });
                }}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {manifest.metrics && manifest.metrics.rows.length > 0 && (
        <Card>
          <CardContent className="py-4 overflow-x-auto">
            <h2 className="text-sm font-semibold mb-2">
              {translate("crm.lab_results.metrics")}
            </h2>
            <table className="text-sm w-full">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-4">
                    {translate("crm.lab_results.arm")}
                  </th>
                  {manifest.metrics.columns.map((column) => (
                    <th
                      key={column.key}
                      className="py-1 pr-4"
                      title={column.help}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {manifest.metrics.rows.map((row) => (
                  <tr key={row.arm} className="border-t">
                    <td className="py-1 pr-4 font-medium">
                      {arms.find((a) => a.id === row.arm)?.label ?? row.arm}
                    </td>
                    {manifest.metrics!.columns.map((column) => (
                      <td key={column.key} className="py-1 pr-4 tabular-nums">
                        {formatMetric(row.values[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {manifest.description_md && (
        <Card>
          <CardContent className="py-4">
            <Markdown>{manifest.description_md}</Markdown>
          </CardContent>
        </Card>
      )}

      {manifest.links && manifest.links.length > 0 && (
        <div className="flex flex-wrap gap-3 text-sm">
          {manifest.links.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}

      {lightbox && photo && (
        <Lightbox
          manifest={manifest}
          arms={arms}
          photos={photos}
          photoIndex={photoIndex}
          armIndex={lightbox.armIndex}
          onChange={(pi, ai) => {
            setPhotoIndex(pi);
            setLightbox({ armIndex: ai });
          }}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

const PhotoStrip = ({
  photos,
  arms,
  manifest,
  selected,
  onSelect,
  pickLabel,
}: {
  photos: ExperimentPhoto[];
  arms: ExperimentArm[];
  manifest: ExperimentManifest;
  selected: number;
  onSelect: (index: number) => void;
  pickLabel: string;
}) => {
  const thumbArm = arms.find((a) => a.kind === "source") ?? arms[0];
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{pickLabel}</p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {photos.map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => onSelect(index)}
            className={`shrink-0 rounded-md overflow-hidden border-2 ${
              index === selected
                ? "border-primary"
                : "border-transparent opacity-80 hover:opacity-100"
            }`}
            title={photo.label ?? photo.id}
          >
            <ExperimentImage
              media={manifest.images[imageKey(photo.id, thumbArm.id)]}
              alt={photo.label ?? photo.id}
              className="h-20 w-auto object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const GridView = ({
  photos,
  arms,
  manifest,
  onOpen,
}: {
  photos: ExperimentPhoto[];
  arms: ExperimentArm[];
  manifest: ExperimentManifest;
  onOpen: (photoIndex: number, armIndex: number) => void;
}) => (
  <div className="overflow-x-auto">
    <table className="border-separate border-spacing-1">
      <thead>
        <tr>
          <th />
          {photos.map((photo) => (
            <th
              key={photo.id}
              className="text-xs font-normal text-muted-foreground px-1 whitespace-nowrap"
            >
              {photo.label ?? photo.id}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {arms.map((arm, armIndex) => (
          <tr key={arm.id}>
            <th className="text-xs text-left font-medium pr-2 whitespace-nowrap align-middle">
              <Badge variant={kindVariant(arm.kind)}>{arm.label}</Badge>
            </th>
            {photos.map((photo, photoIndex) => (
              <td key={photo.id} className="align-middle">
                <ExperimentImage
                  media={manifest.images[imageKey(photo.id, arm.id)]}
                  alt={`${photo.label ?? photo.id} – ${arm.label}`}
                  className="h-28 w-auto max-w-none object-contain cursor-zoom-in rounded-sm"
                  onClick={() => onOpen(photoIndex, armIndex)}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WipeCompare = ({
  photo,
  arms,
  manifest,
}: {
  photo: ExperimentPhoto;
  arms: ExperimentArm[];
  manifest: ExperimentManifest;
}) => {
  const translate = useTranslate();
  const [leftId, setLeftId] = useState(arms[0]?.id);
  const [rightId, setRightId] = useState(
    arms[Math.min(1, arms.length - 1)]?.id,
  );
  const [position, setPosition] = useState(50);
  const left = arms.find((a) => a.id === leftId) ?? arms[0];
  const right = arms.find((a) => a.id === rightId) ?? arms[0];
  const leftUrl = useExperimentImageUrl(
    manifest.images[imageKey(photo.id, left.id)],
  );
  const rightUrl = useExperimentImageUrl(
    manifest.images[imageKey(photo.id, right.id)],
  );
  const ref = manifest.images[imageKey(photo.id, left.id)];
  const aspect =
    ref?.width && ref?.height ? `${ref.width} / ${ref.height}` : "4 / 3";
  const selectClass = "border rounded-md bg-background px-2 py-1 text-sm";
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          {translate("crm.lab_results.wipe_left")}
          <select
            className={selectClass}
            value={left.id}
            onChange={(e) => setLeftId(e.target.value)}
          >
            {arms.map((arm) => (
              <option key={arm.id} value={arm.id}>
                {arm.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          {translate("crm.lab_results.wipe_right")}
          <select
            className={selectClass}
            value={right.id}
            onChange={(e) => setRightId(e.target.value)}
          >
            {arms.map((arm) => (
              <option key={arm.id} value={arm.id}>
                {arm.label}
              </option>
            ))}
          </select>
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={position}
          onChange={(e) => setPosition(Number(e.target.value))}
          className="w-64"
          aria-label={translate("crm.lab_results.wipe_slider")}
        />
      </div>
      <div
        className="relative w-full max-h-[80vh] bg-black/90 rounded-md overflow-hidden select-none"
        style={{ aspectRatio: aspect }}
        onMouseMove={(e) => {
          if (e.buttons !== 1) return;
          const rect = e.currentTarget.getBoundingClientRect();
          setPosition(Math.round(((e.clientX - rect.left) / rect.width) * 100));
        }}
      >
        {rightUrl && (
          <img
            src={rightUrl}
            alt={right.label}
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
        )}
        {leftUrl && (
          <img
            src={leftUrl}
            alt={left.label}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
            draggable={false}
          />
        )}
        <div
          className="absolute inset-y-0 w-0.5 bg-white/80 pointer-events-none"
          style={{ left: `${position}%` }}
        />
        <span className="absolute top-2 left-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
          {left.label}
        </span>
        <span className="absolute top-2 right-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
          {right.label}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("crm.lab_results.wipe_help")}
      </p>
    </div>
  );
};

const Lightbox = ({
  manifest,
  arms,
  photos,
  photoIndex,
  armIndex,
  onChange,
  onClose,
}: {
  manifest: ExperimentManifest;
  arms: ExperimentArm[];
  photos: ExperimentPhoto[];
  photoIndex: number;
  armIndex: number;
  onChange: (photoIndex: number, armIndex: number) => void;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [fit, setFit] = useState(true);
  const photo = photos[photoIndex];
  const arm = arms[armIndex];
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight")
        onChange(photoIndex, (armIndex + 1) % arms.length);
      if (event.key === "ArrowLeft")
        onChange(photoIndex, (armIndex - 1 + arms.length) % arms.length);
      if (event.key === "ArrowDown")
        onChange(Math.min(photoIndex + 1, photos.length - 1), armIndex);
      if (event.key === "ArrowUp")
        onChange(Math.max(photoIndex - 1, 0), armIndex);
      if (event.key === "z" || event.key === "Z") setFit((f) => !f);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [armIndex, arms.length, onChange, onClose, photoIndex, photos.length]);
  if (!photo || !arm) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 text-white flex flex-col"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between gap-4 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Badge
            variant={kindVariant(arm.kind)}
            className={
              arm.kind === "source" ? "text-white border-white/60" : undefined
            }
          >
            {arm.label}
          </Badge>
          <span className="truncate text-white/80">
            {photo.label ?? photo.id} · {armIndex + 1}/{arms.length}
          </span>
          {arm.description && (
            <span className="hidden md:inline text-white/60 truncate">
              — {arm.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:text-white"
            onClick={() => setFit((f) => !f)}
          >
            {fit
              ? translate("crm.lab_results.zoom_100")
              : translate("crm.lab_results.zoom_fit")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:text-white"
            onClick={onClose}
            title="Esc"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        className={`flex-1 min-h-0 ${fit ? "flex items-center justify-center" : "overflow-auto"}`}
      >
        <ExperimentImage
          media={manifest.images[imageKey(photo.id, arm.id)]}
          alt={`${photo.label ?? photo.id} – ${arm.label}`}
          className={
            fit ? "max-w-full max-h-full object-contain" : "max-w-none"
          }
        />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1 px-4 py-2">
        {arms.map((a, i) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onChange(photoIndex, i)}
            className={`px-2 py-1 text-xs rounded ${i === armIndex ? "bg-white text-black" : "bg-white/15 hover:bg-white/30"}`}
          >
            {a.label}
          </button>
        ))}
        <span className="ml-3 text-xs text-white/60">
          {translate("crm.lab_results.lightbox_help")}
        </span>
      </div>
    </div>
  );
};
